import { useMutation } from '@tanstack/react-query';

import { pointTime } from '../../shared/lib/format';
import { Modal } from '../../shared/ui/modal';
import { Note } from '../../shared/ui/note';
import type { IClientSource, TCustomerSecret } from '../../entities/customer';
import { revokeSourceSecret } from '../../entities/source';

interface IRevokeDialogProps {
  secret: TCustomerSecret | null;
  /** Other live secrets of theirs here — what still works once this one is gone. */
  others: number;
  sourceId: string;
  source: IClientSource;
  clientName: string;
  now: number;
  timeZone?: string;
  onClose: () => void;
  onRevoked: () => void;
}

// No replacement is made: this is for a leaked secret, or a client who must stop getting through.
export const RevokeDialog = ({
  secret,
  others,
  sourceId,
  source,
  clientName,
  now,
  timeZone,
  onClose,
  onRevoked,
}: IRevokeDialogProps) => {
  const revoke = useMutation({
    mutationFn: (target: TCustomerSecret) => revokeSourceSecret(sourceId, target.secretId),
    onSuccess: onRevoked,
  });

  const outcome = source.rejects
    ? `every request ${clientName} signs with it is rejected, and no run starts`
    : `what ${clientName} signs with it is delivered marked as failed`;

  return (
    <Modal
      open={secret !== null}
      onOpenChange={(open) => {
        if (open) return;
        revoke.reset();
        onClose();
      }}
      title={`Revoke ${clientName}’s secret?`}
      cancelLabel="Keep it"
      submit={{
        label: 'Revoke it',
        destructive: true,
        onSubmit: () => secret && revoke.mutate(secret),
        waiting: revoke.isPending,
        waitingLabel: 'Revoking',
      }}
    >
      <div className="flex flex-col gap-3">
        <p className="text-data text-primary">
          <span className="font-mono">{secret?.secretHint}</span> stops working at once and nothing
          replaces it.{' '}
          {secret?.lastUsedAt
            ? `It last matched at ${pointTime(secret.lastUsedAt, now, timeZone)}.`
            : 'It has not matched a request here.'}
        </p>
        <Note tone="caution">
          From then on {outcome}.
          {others > 0
            ? ` Their other ${others === 1 ? 'secret keeps' : `${others} secrets keep`} working.`
            : ' To let them back in, add a new secret.'}
        </Note>
        {revoke.isError && (
          <Note tone="danger">Not revoked: {revoke.error.message} It still works.</Note>
        )}
      </div>
    </Modal>
  );
};
